import type { PropsWithChildren } from 'react';
import cx from 'classnames';
import config from '@plone/registry';
import CTA from '@redturtle/qubicaamf-theme/components/Blocks/commons/CTA';

import styles from '@redturtle/qubicaamf-theme/components/Blocks/commons/banner.module.css';
import containerStyles from '@redturtle/qubicaamf-theme/components/Container/container.module.css';
import type { BlocksFormData, JSONSchema } from '@plone/types';
import type { CtaBlockExtender } from '@redturtle/qubicaamf-theme/components/Blocks/extenders/cta';

export interface BannerData
  extends JSONSchema,
    BlocksFormData,
    CtaBlockExtender {
  bannerTitle?: string;
  bannerDescription?: string;
  showOverlay?: boolean;
  bannerTitleAsH1?: boolean;
  bannerSize: string;
  styles?: {
    line_color?: string;
    fullwidth?: boolean;
    add_bottom_line?: boolean;
    add_top_line?: boolean;
  };
}

interface BannerProps {
  data: BannerData;
  editMode?: boolean;
  mediaContainerClassname?: string;
}

const Banner = (props: PropsWithChildren<BannerProps>) => {
  const { data, editMode, mediaContainerClassname, children } = props;
  const Container = config.getComponent('Container').component || 'div';

  return (
    <div
      className={cx('banner', styles.banner, {
        [styles.smallBanner]: data.bannerSize === 'Small' || !data.bannerSize,
        [styles.mediumBanner]: data.bannerSize === 'Medium',
        [styles.largeBanner]: data.bannerSize === 'Large',
        [containerStyles.container]:
          (data.bannerSize === 'Small' || !data.bannerSize) &&
          !data.styles?.fullwidth,
      })}
    >
      <div className={cx(mediaContainerClassname)}>
        {children}
        {data.showOverlay && <div className={styles.darkOverlay} />}
      </div>

      <Container
        className={cx(styles.blockBannerContainer, {
          [styles.editBlockBannerContainer]: editMode,
        })}
      >
        <div
          className={cx(styles.bannerText, {
            [styles.bannerTextNoOverlay]: !data.showOverlay,
          })}
        >
          <div className={styles.bannerTitleContainer}>
            {data.bannerTitleAsH1 ? (
              <h1 className={styles.bannerTitle}>{data.bannerTitle}</h1>
            ) : (
              <h2 className={styles.bannerTitle}>{data.bannerTitle}</h2>
            )}
          </div>
          <div className={styles.bannerDescriptionContainer}>
            <p className={styles.bannerDescription}>{data.bannerDescription}</p>
          </div>
          {data.linkHref?.[0] && (
            <CTA
              href={data.linkHref[0]['@id']}
              onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                if (editMode) {
                  e.preventDefault();
                }
              }}
              openLinkInNewTab={false}
              ctaButtonStyle={data.ctaButtonStyle}
              linkIcon={data.linkIcon}
              cta_color={data.cta_color}
            >
              {data.linkTitle}
            </CTA>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Banner;
